import { useAddressSchema } from "@certn/cascadia";
import { t } from "i18next";
import { boolean, object, string } from "yup";

import { paymentMethods } from "../../types";

interface Props {
  profileNames: (string | null)[];
  streetAddressFieldName?: string;
  skipPaymentProfileValidation?: boolean;
}

export function useBillingProfileSchema({
  profileNames,
  skipPaymentProfileValidation,
}: Props) {
  const addressSchema = useAddressSchema({
    streetAddressFieldName: "address_line_1",
  });

  return object().shape({
    profile_name: string()
      .required()
      .label(t("Profile name"))
      .trim()
      .test(
        "profile-name",
        t("A billing profile with this name already exists"),
        (value) => !profileNames?.includes(value)
      ),
    customer_name: string().required().label(t("Client name")),
    sales_tax_number: string().nullable().label(t("Sales tax number")),
    purchase_order_number: string()
      .nullable()
      .label(t("Purchase order number")),
    currency: string().required().label(t("Currency")),
    invoicing_frequency: string().required().label(t("Invoicing frequency")),
    payer_option: string().required().label(t("Payer")),
    collection_method: string().required().label(t("Collection method")),
    payment_method: string().required().label(t("Payment method")),
    payment_profile_id: string()
      .nullable()
      .when(
        ["payment_method", "payer_option"],
        ([paymentMethod, payerOption], schema) => {
          if (skipPaymentProfileValidation) {
            return schema;
          }
          if (
            paymentMethod === paymentMethods.CREDIT_CARD &&
            payerOption !== "APPLICANT"
          ) {
            return schema.required().label(t("Payment profile"));
          }
          return schema;
        }
      ),
    address: addressSchema,
    single_use_po: boolean(),
  });
}
